import { ScaffoldModule, Question, ScaffoldConfig, InstallResult, FileChange, FileOperations, Framework } from '@scaffit/core';
import { join } from 'path';


// CLI Integration (ScaffoldModule)
const scaffold: ScaffoldModule = {
  name: 'env',
  description: 'Framework-agnostic environment variable setup with validation using Zod',
  category: 'Foundation',
  tags: ['environment', 'validation', 'config', 'zod', 'framework-agnostic', 'nextjs', 'react', 'express', 'vite', 'nodejs'],

  prompts: async (): Promise<Question[]> => [
    {
      type: 'confirm',
      name: 'includeExamples',
      message: 'Include example environment variables?',
      default: true
    },
    {
      type: 'confirm',
      name: 'useZod',
      message: 'Use Zod validation for environment variables?',
      default: true
    },
    {
      type: 'multiselect',
      name: 'features',
      message: 'Select features to include:',
      choices: [
        'database',
        'auth',
        'apis',
        'email',
        'storage',
        'analytics'
      ]
    }
  ],

  install: async (config: ScaffoldConfig): Promise<InstallResult> => {
    const result: InstallResult = {
      success: true,
      filesChanged: [],
      dependencies: {
        dev: [],
        prod: []
      },
      errors: [],
      warnings: []
    };

    try {
      const projectRoot = config.projectInfo?.rootDir || process.cwd();
      const framework = await FileOperations.detectFramework(projectRoot);
      const features = config.features || [];

      // Create .env.example file
      const envExamplePath = join(projectRoot, '.env.example');
      const envExampleContent = generateEnvExample(framework, features);

      await FileOperations.writeFile(envExamplePath, envExampleContent);
      result.filesChanged.push({
        path: '.env.example',
        action: 'created',
        content: envExampleContent
      });

      // Create env validation file if Zod is enabled
      if (config.useZod) {
        const envFilePath = join(projectRoot, 'env.ts');
        const envContent = generateZodSchema(framework, features);

        await FileOperations.writeFile(envFilePath, envContent);
        result.filesChanged.push({
          path: 'env.ts',
          action: 'created',
          content: envContent
        });

        // Add zod dependency to package.json
        const packageJsonPath = join(projectRoot, 'package.json');
        if (await FileOperations.fileExists(packageJsonPath)) {
          const packageJsonContent = await FileOperations.readFile(packageJsonPath);
          if (!packageJsonContent) {
            result.errors.push('Failed to read package.json');
            return result;
          }
          
          const packageJson = JSON.parse(packageJsonContent);
          if (!packageJson.dependencies) {
            packageJson.dependencies = {};
          }
          if (!packageJson.dependencies.zod) {
            packageJson.dependencies.zod = '^3.22.0';
            await FileOperations.writeFile(packageJsonPath, JSON.stringify(packageJson, null, 2));
            result.dependencies.prod.push('zod');
          }
        }
      }

      result.success = result.errors.length === 0;
      result.message = 'Environment setup complete! Copy .env.example to .env.local and fill in your values.';
      return result;

    } catch (error) {
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
      return result;
    }
  },

  getPreview: async (config: ScaffoldConfig): Promise<FileChange[]> => {
    const changes: FileChange[] = [];

    // Preview .env.example
    changes.push({
      path: '.env.example',
      action: 'created',
      content: `# Environment Variables
# Copy this file to .env.local and fill in your values

# Database
DATABASE_URL="postgresql://username:password@localhost:5432/database"

# Authentication
NEXTAUTH_SECRET="your-secret-key-here"
NEXTAUTH_URL="http://localhost:3000"

# API Keys
OPENAI_API_KEY="your-openai-key-here"
STRIPE_SECRET_KEY="your-stripe-secret-key"

# App Configuration
NODE_ENV="development"
PORT=3000`
    });

    // Preview env validation file if Zod is enabled
    if (config.useZod) {
      changes.push({
        path: 'env.ts',
        action: 'created',
        content: `import { z } from 'zod';

const envSchema = z.object({
  NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
  PORT: z.string().default('3000'),
  DATABASE_URL: z.string().url(),
  NEXTAUTH_SECRET: z.string().min(32),
  NEXTAUTH_URL: z.string().url(),
  OPENAI_API_KEY: z.string().optional(),
  STRIPE_SECRET_KEY: z.string().optional(),
});

export const env = envSchema.parse(process.env);

export type Env = z.infer<typeof envSchema>;`
      });

      changes.push({
        path: 'package.json',
        action: 'modified',
        content: '+ "zod": "^3.22.0"'
      });
    }

    return changes;
  }
};

// Helper functions
function generateEnvExample(framework: Framework, features: string[]): string {
  const lines = ['# Environment Variables', '# Copy this file to .env.local and fill in your values', ''];
  
  // Framework-specific variables
  switch (framework) {
    case 'nextjs':
      lines.push('# Next.js Configuration');
      lines.push('NEXTAUTH_URL="http://localhost:3000"');
      lines.push('NEXTAUTH_SECRET="your-secret-key-here"');
      break;
    case 'express':
      lines.push('# Express Configuration');
      lines.push('PORT=3000');
      lines.push('NODE_ENV="development"');
      break;
    case 'react-vite':
    case 'react-cra':
      lines.push('# React Configuration');
      lines.push('VITE_API_URL="http://localhost:3000"');
      break;
    case 'vue':
      lines.push('# Vue Configuration');
      lines.push('VITE_API_URL="http://localhost:3000"');
      break;
    case 'angular':
      lines.push('# Angular Configuration');
      lines.push('API_URL="http://localhost:3000"');
      break;
    case 'svelte':
      lines.push('# Svelte Configuration');
      lines.push('VITE_API_URL="http://localhost:3000"');
      break;
    case 'fastify':
      lines.push('# Fastify Configuration');
      lines.push('PORT=3000');
      lines.push('NODE_ENV="development"');
      break;
    case 'nodejs':
    default:
      lines.push('# Node.js Configuration');
      lines.push('NODE_ENV="development"');
      lines.push('PORT=3000');
  }
  
  lines.push('');
  
  // Feature-specific variables
  if (features.includes('database')) {
    lines.push('# Database');
    lines.push('DATABASE_URL="postgresql://username:password@localhost:5432/database"');
    lines.push('');
  }
  
  if (features.includes('auth')) {
    lines.push('# Authentication');
    if (framework === 'nextjs') {
      lines.push('NEXTAUTH_SECRET="your-secret-key-here"');
      lines.push('NEXTAUTH_URL="http://localhost:3000"');
    } else {
      lines.push('JWT_SECRET="your-jwt-secret-here"');
      lines.push('SESSION_SECRET="your-session-secret-here"');
    }
    lines.push('');
  }
  
  if (features.includes('apis')) {
    lines.push('# API Keys');
    lines.push('OPENAI_API_KEY="your-openai-key-here"');
    lines.push('STRIPE_SECRET_KEY="your-stripe-secret-key"');
    lines.push('');
  }
  
  if (features.includes('email')) {
    lines.push('# Email Service');
    lines.push('SMTP_HOST="smtp.gmail.com"');
    lines.push('SMTP_PORT=587');
    lines.push('SMTP_USER="your-email@gmail.com"');
    lines.push('SMTP_PASS="your-app-password"');
    lines.push('');
  }
  
  if (features.includes('storage')) {
    lines.push('# File Storage');
    lines.push('AWS_ACCESS_KEY_ID="your-aws-access-key"');
    lines.push('AWS_SECRET_ACCESS_KEY="your-aws-secret-key"');
    lines.push('AWS_REGION="us-east-1"');
    lines.push('AWS_BUCKET_NAME="your-bucket-name"');
    lines.push('');
  }
  
  if (features.includes('analytics')) {
    lines.push('# Analytics');
    lines.push('GOOGLE_ANALYTICS_ID="GA-XXXXXXXXX"');
    lines.push('POSTHOG_KEY="your-posthog-key"');
    lines.push('');
  }
  
  return lines.join('\n');
}

function generateZodSchema(framework: Framework, features: string[]): string {
  const lines = ['import { z } from \'zod\';', ''];
  
  // Base schema
  const schemaFields = ['NODE_ENV: z.enum([\'development\', \'production\', \'test\']).default(\'development\')'];
  
  // Framework-specific fields
  switch (framework) {
    case 'nextjs':
      schemaFields.push('NEXTAUTH_URL: z.string().url()');
      schemaFields.push('NEXTAUTH_SECRET: z.string().min(32)');
      break;
    case 'express':
    case 'fastify':
      schemaFields.push('PORT: z.string().default(\'3000\')');
      break;
    case 'react-vite':
    case 'react-cra':
    case 'vue':
    case 'svelte':
      schemaFields.push('VITE_API_URL: z.string().url()');
      break;
    case 'angular':
      schemaFields.push('API_URL: z.string().url()');
      break;
    case 'nodejs':
    default:
      schemaFields.push('PORT: z.string().default(\'3000\')');
  }
  
  // Feature-specific fields
  if (features.includes('database')) {
    schemaFields.push('DATABASE_URL: z.string().url()');
  }
  
  if (features.includes('auth')) {
    if (framework === 'nextjs') {
      schemaFields.push('NEXTAUTH_SECRET: z.string().min(32)');
      schemaFields.push('NEXTAUTH_URL: z.string().url()');
    } else {
      schemaFields.push('JWT_SECRET: z.string().min(32)');
      schemaFields.push('SESSION_SECRET: z.string().min(32)');
    }
  }
  
  if (features.includes('apis')) {
    schemaFields.push('OPENAI_API_KEY: z.string().optional()');
    schemaFields.push('STRIPE_SECRET_KEY: z.string().optional()');
  }
  
  if (features.includes('email')) {
    schemaFields.push('SMTP_HOST: z.string()');
    schemaFields.push('SMTP_PORT: z.string()');
    schemaFields.push('SMTP_USER: z.string().email()');
    schemaFields.push('SMTP_PASS: z.string()');
  }
  
  if (features.includes('storage')) {
    schemaFields.push('AWS_ACCESS_KEY_ID: z.string().optional()');
    schemaFields.push('AWS_SECRET_ACCESS_KEY: z.string().optional()');
    schemaFields.push('AWS_REGION: z.string().optional()');
    schemaFields.push('AWS_BUCKET_NAME: z.string().optional()');
  }
  
  if (features.includes('analytics')) {
    schemaFields.push('GOOGLE_ANALYTICS_ID: z.string().optional()');
    schemaFields.push('POSTHOG_KEY: z.string().optional()');
  }
  
  lines.push('const envSchema = z.object({');
  schemaFields.forEach(field => {
    lines.push(`  ${field},`);
  });
  lines.push('});');
  lines.push('');
  lines.push('export const env = envSchema.parse(process.env);');
  lines.push('');
  lines.push('export type Env = z.infer<typeof envSchema>;');
  
  return lines.join('\n');
}

// Standalone usage functions for direct npm package usage
// Note: Requires @scaffit/core to be installed
export async function setupEnv(options: {
  includeExamples?: boolean;
  useZod?: boolean;
  features?: string[];
  projectRoot?: string;
} = {}): Promise<InstallResult> {
  const config: ScaffoldConfig = {
    includeExamples: options.includeExamples ?? true,
    useZod: options.useZod ?? true,
    features: options.features ?? ['database', 'auth', 'apis'],
    projectInfo: {
      rootDir: options.projectRoot || process.cwd()
    }
  };
  
  return scaffold.install(config);
}

export async function previewEnv(options: {
  includeExamples?: boolean;
  useZod?: boolean;
  features?: string[];
} = {}): Promise<FileChange[]> {
  const config: ScaffoldConfig = {
    includeExamples: options.includeExamples ?? true,
    useZod: options.useZod ?? true,
    features: options.features ?? ['database', 'auth', 'apis']
  };
  
  return scaffold.getPreview!(config);
}

export default scaffold;